<template>
  <AnimatedContainer>
    <header class="flex min-h-screen flex-col">
      <div class="relative flex grow flex-col">
        <!-- IMAGE OR VIDEO -->
        <div class="absolute inset-0 -z-10 overflow-hidden">
          <AppImage
            v-if="imageOrVideo.image"
            :src="imageOrVideo.image"
            :alt="
              $i18n.locale === 'fr'
                ? imageOrVideo.image.alt_fr
                : imageOrVideo.image.alt
            "
            class="h-full w-full object-cover"
            :style="`object-position: ${imageOrVideo.image.focus_css};`"
          />

          <AppImage
            v-else-if="
              imageOrVideo.video &&
              data.hero_only_image &&
              imageOrVideo.cover_image
            "
            :src="imageOrVideo.cover_image"
            class="h-full w-full object-cover"
            :style="`object-position: ${imageOrVideo.cover_image.focus_css};`"
          />

          <VideoPlayer
            v-else-if="imageOrVideo.video"
            :src="imageOrVideo.video"
            :autoplay="true"
            class="h-full w-full scale-[1.2] transition-transform"
          />
          <div
            v-else
            class="h-full w-full bg-gradient-to-tr from-blue-dark-800 to-blue-dark-600"
          />
        </div>

        <!-- GRADIENT TOP -->
        <div
          class="absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-[#011F2E] opacity-[0.73]"
        />

        <!-- GRADIENT BOTTOM -->
        <div
          class="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-[#022638] opacity-[0.80]"
        />

        <div
          class="z-10 flex w-full grow flex-col items-center justify-center overflow-hidden pb-12 pt-navigation-desktop text-white"
        >
          <!-- ICON -->
          <div
            class="fade-in relative my-auto w-full max-w-xl px-10 py-20 xs:px-24 sm:px-36 lg:max-w-3xl"
          >
            <template v-if="data.hero_icon">
              <div
                class="radial-gradient absolute inset-[-20%] z-0 overflow-hidden"
              />
              <AppImage
                :src="data.hero_icon"
                class="relative z-10 h-full max-h-32 w-full object-contain sm:max-h-48"
                :alt="
                  locale === 'fr' ? data.hero_icon.alt_fr : data.hero_icon.alt
                "
              />
            </template>
          </div>

          <!-- TITLE -->
          <Container
            indent="wide"
            class="flex w-full items-end justify-between gap-10"
          >
            <div>
              <h1
                v-if="data.heading"
                class="fade-in max-w-title hyphens-auto text-3xl font-medium leading-9 sm:mb-6 sm:text-6xl"
                v-html="$replaceHtmlTags(data.heading)"
              />
            </div>

            <button
              class="slide-down group -mt-1 hidden shrink-0 items-center gap-2 text-sm lg:flex"
              :aria-label="$t('accessibility.scroll_to_content')"
              @click="scrollTo()"
            >
              <span>{{ $t('learn_more') }}</span>
              <ArrowGrowIcon
                animate
                stroke-width="2"
                class="w-6 translate-y-1 rotate-90"
              />
            </button>
          </Container>
        </div>
      </div>

      <!-- SPONSOR AND ORGANIZER LOGOS -->
      <div
        v-if="amountSponsorsOrOrganizers"
        class="min-h-[8rem] w-full bg-blue-10 pb-7 pt-4"
      >
        <AnimatedContainer>
          <div
            :class="
              amountSponsorsOrOrganizers > 3
                ? 'mx-auto w-full max-w-[68rem] px-container sm:px-container-desktop xl:sm:px-container-desktop-wide xl:mx-0 xl:max-w-none'
                : 'mx-auto w-full max-w-[68rem] px-container sm:px-container-desktop'
            "
          >
            <div
              class="flex flex-col justify-between gap-x-8 gap-y-4 md:gap-x-16"
              :class="
                amountSponsorsOrOrganizers > 3 ? 'xl:flex-row' : 'md:flex-row'
              "
            >
              <!-- MAIN SPONSORS -->
              <div v-if="data?.main_sponsors" class="flex flex-col">
                <p class="mb-3 text-sm opacity-60">
                  {{ data.main_sponsors_title ?? $t('main_sponsors') }}
                </p>
                <div class="flex grow gap-4 md:gap-10">
                  <div
                    v-for="sponsor in data.main_sponsors"
                    :key="sponsor.id"
                    class="flex max-w-[15rem] items-center"
                  >
                    <NuxtLink
                      :to="sponsor.link"
                      :target="$useTarget(sponsor.link)"
                      class="slide-in-left max-h-[4rem]"
                    >
                      <AppImage
                        v-if="sponsor.logo"
                        :alt="
                          $i18n.locale === 'fr'
                            ? sponsor.logo.alt_fr
                            : sponsor.logo.alt
                        "
                        :src="sponsor.logo"
                      />
                    </NuxtLink>
                  </div>
                </div>
              </div>
              <!-- ORGANIZERS -->
              <div v-if="data?.organizers" class="flex flex-col">
                <p class="mb-3 text-sm opacity-60">
                  {{ data.organizers_title ?? $t('organizers') }}
                </p>
                <div class="flex w-full grow gap-4 md:gap-10">
                  <div
                    v-for="organizer in data.organizers"
                    :key="organizer.id"
                    class="flex max-w-[15rem] items-center"
                  >
                    <NuxtLink
                      :to="organizer.link"
                      :target="$useTarget(organizer.link)"
                      class="slide-in-left max-h-[4rem]"
                    >
                      <AppImage
                        v-if="organizer.logo"
                        :alt="
                          $i18n.locale === 'fr'
                            ? organizer.logo.alt_fr
                            : organizer.logo.alt
                        "
                        :src="organizer.logo"
                      />
                    </NuxtLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedContainer>
      </div>
    </header>
    <!-- LEAD -->
    <Container v-if="data.lead" indent="center">
      <div class="max-w-text pt-16 text-md font-medium" v-html="data.lead" />
    </Container>
  </AnimatedContainer>
</template>

<script setup>
const { t } = useI18n()
const { locale } = useI18n()

const props = defineProps({
  data: { type: Object, required: true },
})

// COMPUTED
const imageOrVideo = computed(() => {
  return props.data?.image_or_video?.[0] || false
})

const amountSponsorsOrOrganizers = computed(() => {
  return (
    (props.data?.main_sponsors?.length || 0) +
    (props.data?.organizers?.length || 0)
  )
})

// FUNCTIONS
function formateDate(date) {
  if (!date) {
    return ''
  }

  date = new Date(date)

  const dateOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }

  const timeOptions = {
    hour: '2-digit',
    minute: '2-digit',
  }

  const formattedDate = date.toLocaleDateString('de', dateOptions)
  const formattedTime = date.toLocaleTimeString('de', timeOptions)
  const customSeparator = ' '

  return formattedDate + customSeparator + formattedTime + ` ${t('o_clock')}`
}

const scrollTo = () => {
  const selector = '[scroll-to-index="0"]'
  const element = document.querySelector(selector)

  if (!element) {
    return
  }

  const headerOffset = 250
  const elementPosition = element.offsetTop
  const offsetPosition = elementPosition - headerOffset

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  })
}
</script>

<style lang="scss" scoped>
.radial-gradient {
  background: radial-gradient(
    farthest-side at 50% 50%,
    rgba(2, 38, 56, 0.4) 0%,
    rgba(0, 0, 0, 0) 100%
  );
}
</style>
